import { showHttpError, showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { rootApi } from 'root-api'

import { bulkApplyManagedEvents, getOrganizationMembershipsWithSlugConflicts } from '../api'
import { selectedIdsSelector } from './selectors'

const openSlugConflictsPopup = createAction('applyManagedEventsPopup/openSlugConflictsPopup')
export const closeSlugConflictsPopup = createAction('applyManagedEventsPopup/closeSlugConflictsPopup')

export const applyManagedEvents = createAsyncThunk(
  'applyManagedEventsPopup/apply',
  async ({ membershipIds, checkSlugConflicts }, { dispatch, getState, rejectWithValue }) => {
    const state = getState()
    const selectedManagedEventIds = selectedIdsSelector(state)

    if (checkSlugConflicts) {
      const [slugConflictsSuccess, membershipsWithConflictResponse] = await getOrganizationMembershipsWithSlugConflicts(
        selectedManagedEventIds,
        membershipIds,
      )

      if (!slugConflictsSuccess) {
        dispatch(showHttpError(membershipsWithConflictResponse))
        return rejectWithValue({})
      }

      if (membershipsWithConflictResponse.conflicts.length > 0) {
        dispatch(openSlugConflictsPopup(membershipsWithConflictResponse))
        return rejectWithValue({})
      }
    }

    const [success, response] = await bulkApplyManagedEvents(
      selectedManagedEventIds,
      membershipIds
    )

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue({})
    }

    dispatch(showSuccess(I18n.t(
      'publisher.organization.members.bulk_managed_events_applying.success_notice',
      {
        count: membershipIds.length,
        managed_events_count: selectedManagedEventIds.length,
      }
    )))

    // Invalidate the RTK Query cache to refresh the managed event types list
    dispatch(rootApi.util.invalidateTags(['MANAGED_EVENT_TYPE_LIST']))

    return true
  }
)

const slice = createSlice({
  name: 'applyManagedEventsPopup',
  initialState: {
    state: 'close',
    membershipIds: [],
    selectedIds: [],
    isSubmitting: false,
    isTouched: false,
    membershipsWithSlugConflict: []
  },
  reducers: {
    openApplyManagedEventsPopup(state, { payload: membershipIds }) {
      state.state = 'open'
      state.membershipIds = membershipIds
    },
    closeApplyManagedEventsPopup(state) {
      state.state = 'close'
      state.isOpen = false
    },
    clearSelection(state) {
      state.selectedIds = []
    },
    setSelectedIds: (state, { payload: selectedIds }) => {
      state.selectedIds = selectedIds
      state.isTouched = false
    },
    toggleSelection: (state, { payload: id }) => {
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter((selectedId) => id !== selectedId)
      } else {
        state.selectedIds.push(id)
      }
      state.isTouched = false
    },
    setTouched(state) {
      state.isTouched = true
    }
  },
  extraReducers: (builder) => {
    builder.addCase(applyManagedEvents.pending, (state) => {
      state.isSubmitting = true
    })
    builder.addCase(applyManagedEvents.fulfilled, (state) => {
      state.isSubmitting = false
      state.state = 'close'
      state.selectedIds = []
    })
    builder.addCase(applyManagedEvents.rejected, (state) => {
      state.isSubmitting = false
    })
    builder.addCase(openSlugConflictsPopup, (state, action) => {
      state.state = 'slug_conflict'
      state.membershipsWithSlugConflict = action.payload
    })
    builder.addCase(closeSlugConflictsPopup, (state) => {
      state.state = 'open'
      state.membershipsWithSlugConflict = []
    })
  }
})

export const { reducer, actions: {
  openApplyManagedEventsPopup,
  closeApplyManagedEventsPopup,
  clearSelection,
  setSelectedIds,
  setTouched,
  toggleSelection,
} } = slice
